import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { toast } from "sonner";
import { Plus, Trash2, MapPinned } from "lucide-react";

export default function AdminAreas() {
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/admin/areas").then(r => setAreas(r.data)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const add = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    try {
      await api.post("/admin/areas", { name: name.trim() });
      toast.success(`${name} added — it will now show in nomination and search filters`);
      setName("");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not add area");
    } finally { setBusy(false); }
  };

  const remove = async (n, count) => {
    if (count > 0) { toast.error(`Cannot remove — ${count} venue(s) still use "${n}"`); return; }
    if (!window.confirm(`Remove area "${n}"?`)) return;
    try { await api.delete(`/admin/areas/${encodeURIComponent(n)}`); toast.success("Area removed"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not delete"); }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <Link to="/dashboard" className="text-sm text-gray-700 hover:text-blue-700">← Back to admin console</Link>
      <div className="mt-2 flex items-center gap-3">
        <div className="w-12 h-12 rounded-lg bg-blue-600 text-white flex items-center justify-center"><MapPinned className="w-6 h-6"/></div>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold text-gray-900">Manage areas</h1>
      </div>
      <p className="mt-3 text-gray-700 max-w-2xl">
        Add a new town, city or region to launch Access Local somewhere new. Areas appear on the nomination and search filters instantly.
      </p>

      <Card className="mt-8 border-2 border-gray-900 rounded-xl">
        <CardContent className="p-6">
          <form onSubmit={add} className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[220px]">
              <Label htmlFor="area-name" className="text-lg font-medium">New area name</Label>
              <Input id="area-name" required value={name} onChange={e => setName(e.target.value)}
                placeholder="e.g. Bristol" className="mt-1 h-12 border-2 border-gray-300 focus:border-blue-600" data-testid="new-area-input"/>
            </div>
            <Button type="submit" disabled={busy} className="h-12 rounded-full bg-blue-600 hover:bg-blue-700" data-testid="add-area-btn">
              <Plus className="w-4 h-4 mr-1"/>{busy ? "Adding…" : "Add area"}
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card className="mt-6 rounded-xl border border-gray-200 overflow-hidden">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Area</TableHead>
            <TableHead>Venues in this area</TableHead>
            <TableHead className="w-40"></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {loading && <TableRow><TableCell colSpan={3} className="text-center text-gray-500 py-6">Loading…</TableCell></TableRow>}
            {!loading && areas.length === 0 && <TableRow><TableCell colSpan={3} className="text-center text-gray-500 py-6">No areas yet.</TableCell></TableRow>}
            {areas.map(a => (
              <TableRow key={a.id}>
                <TableCell className="font-medium">{a.name}</TableCell>
                <TableCell className="text-sm text-gray-700">{a.venue_count}</TableCell>
                <TableCell>
                  <Button variant="ghost" size="sm" onClick={() => remove(a.name, a.venue_count)}
                    disabled={a.venue_count > 0}
                    className={`${a.venue_count > 0 ? "text-gray-400" : "text-red-600 hover:bg-red-50"}`}
                    data-testid={`remove-area-${a.name}`}>
                    <Trash2 className="w-4 h-4 mr-1"/>Remove
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
